import React from 'react';

interface ForHealthWellnessSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromBlue: string;
            toPurple: string;
        };
    };
}

const ForHealthWellnessSection: React.FC<ForHealthWellnessSectionProps> = ({ highlightColor }) => {
    const features = [
        {
            title: 'Seamless Data Integration for Clients',
            desc: 'Easily add your clients\' wearable, testing, and performance data.',
            image: '/images/services-health-wellness.jpg'
        },
        {
            title: 'Physical Therapy',
            desc: 'Improve patient side care by consolidating reports and insights for your clients. Track performance and care beyond the appointment.',
            image: '/images/services-health-metrics.jpg'
        },
        {
            title: 'Personal Trainers',
            desc: 'Push performance and optimize recovery through our adaptive data exploration models that account for your prescribed training.',
            image: '/images/services-performance-tracking.jpg'
        }
    ];

    return (
        <section className="pb-32 px-4 sm:px-6 lg:px-8 bg-black relative">
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="mb-12 observe-fade opacity-0 text-center">
                    <div className="flex items-center justify-center">
                        <img
                            src={'/images/services-analytics-platform.png'}
                            className="w-auto h-32 object-cover"
                        />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Health and Wellness</h2>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                        <span 
                            className="bg-clip-text text-transparent"
                            style={{
                                backgroundImage: `linear-gradient(to right, ${highlightColor.gradient.fromBlue}, ${highlightColor.gradient.toPurple})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}
                        >
                            Power your Clients and Analytics
                        </span>
                    </h3>
                    <h4 className="text-xl md:text-2xl font-semibold text-gray-300 mb-6">
                        Your Intelligent Analytics Layer
                    </h4>
                    <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
                        Connect multiple wearable devices and get AI-powered insights that help optimize your clients' performance and recovery. One platform for all their health and wellness data.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-4">
                    {features.map((feature, index) => (
                        <div key={index} className="bg-neutral-900 rounded-xl overflow-hidden observe-fade opacity-0 border border-neutral-800 text-center" style={{ animationDelay: `${index * 0.2}s` }}>
                            <img
                                src={feature.image}
                                alt={feature.title}
                                className="w-full h-48 object-cover"
                            />
                            <div className="p-6">
                                <h6 className="text-lg font-bold text-white mb-2">{feature.title}</h6>
                                <p className="text-gray-300">{feature.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ForHealthWellnessSection;

